import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Users, Car, ArrowUpRight, Check } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { portraits: Camera, gatherings: Users, cars: Car };

const FALLBACK = {
    hero: {
        eyebrow: "Austin, Texas · Booking 2026",
        headline_lead: "Photographs that feel",
        headline_emphasis: "remembered",
        headline_trail: ", not staged.",
        subtext: "I'm Will — a freelance photographer specializing in editorial portraits, intimate gatherings, and the cars people fall in love with.",
        image: "https://images.unsplash.com/photo-1606143412458-acc5f86de897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxlZGl0b3JpYWwlMjBwb3J0cmFpdCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4MjY1MjUyMHww&ixlib=rb-4.1.0&q=85",
        frame_label: "Frame",
        frame_value: "No. 04 · Ilford HP5",
    },
    services: [],
};

export default function Home() {
    const [content, setContent] = useState(FALLBACK);

    useEffect(() => {
        api.get("/site-content")
            .then((res) => setContent(res.data))
            .catch(() => setContent(FALLBACK));
    }, []);

    const hero = content.hero || FALLBACK.hero;
    const services = content.services || [];

    return (
        <div data-testid="home-page" className="bg-[#F9F8F6]">
            {/* HERO */}
            <section data-testid="hero-section" className="relative overflow-hidden film-grain">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7 relative z-10">
                        <p className="overline fade-up" data-testid="hero-eyebrow">{hero.eyebrow}</p>
                        <h1
                            data-testid="hero-headline"
                            className="font-serif font-light text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight mt-6 fade-up delay-1"
                        >
                            {hero.headline_lead}{" "}
                            <em className="italic text-[#A0522D]">{hero.headline_emphasis}</em>
                            {hero.headline_trail}
                        </h1>
                        <p
                            data-testid="hero-subtext"
                            className="mt-8 max-w-xl text-base md:text-lg text-[#5C5853] leading-relaxed fade-up delay-2"
                        >
                            {hero.subtext}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 fade-up delay-3">
                            <Link to="/contact" data-testid="hero-book-btn" className="btn-editorial primary">
                                Book a session <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link to="/portfolio" data-testid="hero-portfolio-btn" className="btn-editorial">
                                See the work
                            </Link>
                        </div>
                    </div>

                    <div className="md:col-span-5 relative fade-up delay-2">
                        <div className="relative aspect-[3/4] overflow-hidden film-grain bg-[#F2EFE9]">
                            {hero.image && (
                                <img
                                    src={hero.image}
                                    alt="Hero"
                                    data-testid="hero-image"
                                    className="w-full h-full object-cover"
                                />
                            )}
                        </div>
                        {(hero.frame_label || hero.frame_value) && (
                            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#F9F8F6] border border-[#E5DED5] px-5 py-4">
                                {hero.frame_label && <p className="overline">{hero.frame_label}</p>}
                                {hero.frame_value && <p className="font-serif text-xl mt-1">{hero.frame_value}</p>}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* SECTION INTRO */}
            <section className="border-t border-[#E5DED5] bg-[#F2EFE9]/40">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <p className="overline" data-testid="services-eyebrow">What I Shoot</p>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
                            Three things, done with care.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:col-start-6">
                        <p className="text-[#5C5853] text-base md:text-lg leading-relaxed">
                            I keep my offering tight on purpose. Every session is collaborative, unhurried, and built around what the photographs are <em className="italic">for</em>. Below is what a typical engagement looks like — bespoke packages are welcome.
                        </p>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section data-testid="services-section" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 space-y-24 md:space-y-32">
                {services.map((s, idx) => (
                    <ServiceBlock key={s.slug} service={s} index={idx} />
                ))}
            </section>

            {/* CTA BAND */}
            <section
                data-testid="cta-section"
                className="border-t border-[#E5DED5] bg-[#1A1817] text-[#F9F8F6]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-8">
                        <p className="overline text-[#A0522D]">Booking</p>
                        <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-4 leading-[1.05]">
                            Have a project in mind? Let's make something honest together.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            data-testid="cta-section-btn"
                            className="btn-editorial primary bg-[#F9F8F6] !text-[#1A1817] hover:!bg-[#A0522D] hover:!text-[#F9F8F6] !border-[#F9F8F6]"
                        >
                            Start a conversation <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ServiceBlock({ service, index }) {
    const Icon = ICONS[service.slug] || Camera;
    const imageLeft = index % 2 === 0;
    return (
        <article
            data-testid={`service-${service.slug}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
        >
            <div className={`md:col-span-7 ${imageLeft ? "md:order-1" : "md:order-2"}`}>
                <div className="relative aspect-[4/3] overflow-hidden film-grain bg-[#F2EFE9]">
                    {service.image && (
                        <img
                            src={service.image}
                            alt={service.title}
                            data-testid={`service-${service.slug}-image`}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                    )}
                </div>
            </div>
            <div className={`md:col-span-5 ${imageLeft ? "md:order-2" : "md:order-1"}`}>
                <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-[#A0522D]" />
                    <p className="overline" data-testid={`service-${service.slug}-eyebrow`}>{service.eyebrow}</p>
                </div>
                <h3 className="font-serif text-4xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
                    {service.title}
                </h3>
                <p className="text-[#5C5853] text-base md:text-lg leading-relaxed mt-5 whitespace-pre-wrap">
                    {service.description}
                </p>

                <div className="mt-8 flex items-baseline gap-3 border-y border-[#E5DED5] py-5 flex-wrap">
                    <p className="overline">Starts at</p>
                    <p className="font-serif text-4xl tracking-tight" data-testid={`service-${service.slug}-price`}>
                        {service.price}
                    </p>
                    <p className="text-sm text-[#5C5853]">{service.unit}</p>
                </div>

                <ul className="mt-6 space-y-3">
                    {(service.includes || []).map((i, k) => (
                        <li key={`${service.slug}-${k}`} className="flex gap-3 text-sm text-[#1A1817]">
                            <Check className="w-4 h-4 mt-1 text-[#A0522D] flex-shrink-0" />
                            <span>{i}</span>
                        </li>
                    ))}
                </ul>

                <Link
                    to="/contact"
                    data-testid={`service-${service.slug}-book-btn`}
                    className="btn-editorial mt-8"
                >
                    Inquire about {(service.title || "this service").toLowerCase()} <ArrowUpRight className="w-4 h-4" />
                </Link>
            </div>
        </article>
    );
}
